import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { IconButton, Tooltip } from "@mui/material";
import { marked } from "marked";
import { useState } from "react";

import { logError } from "../utils/error";

interface CopyButtonProps {
  text: string;
  tooltipTitle?: string;
  size?: "small" | "medium" | "large";
  richText?: boolean;
}

/**
 * Convert markdown text to HTML with basic styling
 */
const convertMarkdownToHTML = (markdownText: string): string => {
  // Configure marked for safe HTML output with styling
  marked.setOptions({
    gfm: true,
    breaks: true
  });

  // Convert markdown to HTML
  const htmlContent = marked.parse(markdownText);

  // Wrap with styling to match the application's theme
  return `
    <div style="font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; font-size: 14px; line-height: 1.5;">
      ${htmlContent}
    </div>
  `;
};

const CopyButton = ({
  text,
  tooltipTitle = "Copy to clipboard",
  size = "small",
  richText = true
}: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (richText && navigator.clipboard && window.ClipboardItem) {
        // Convert markdown to HTML for rich text copying
        const htmlContent = convertMarkdownToHTML(text);

        // Create blobs for each format
        const htmlBlob = new Blob([htmlContent], { type: "text/html" });
        const textBlob = new Blob([text], { type: "text/plain" });

        // Create ClipboardItem with multiple formats
        const clipboardItem = new ClipboardItem({
          "text/html": htmlBlob,
          "text/plain": textBlob
        });

        // Write to clipboard with multiple formats
        await navigator.clipboard.write([clipboardItem]);
      } else {
        // Fallback to plain text for browsers without ClipboardItem support
        await navigator.clipboard.writeText(text);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logError("Failed to copy text", err);
      // Final fallback attempt
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        logError("Fallback copy also failed", fallbackErr);
      }
    }
  };

  return (
    <Tooltip title={copied ? "Copied!" : tooltipTitle}>
      <IconButton onClick={handleCopy} size={size} color={copied ? "success" : "default"}>
        {copied ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
      </IconButton>
    </Tooltip>
  );
};

export default CopyButton;
